import { MoreVert } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  PopoverOrigin,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { backend, BlockIgnoreReason, LensHit } from "Backend";
import Pendo from "helpers/Pendo";
import { useSnackbar } from "notistack";
import { FC, MouseEvent, useState } from "react";
import { P } from "theme/typography";

interface LensHitActionsProps {
  hit: LensHit;
  lensId: number;
  caseId: number;
  onHitIgnored: (hit: LensHit) => void;
  ignoreCollapsedHits: boolean;
  popoverOrigin?: {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
  };
}

const LensHitActions: FC<LensHitActionsProps> = ({ hit, onHitIgnored, ignoreCollapsedHits, popoverOrigin }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reason, setReason] = useState<BlockIgnoreReason>("Duplicate");
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setConfirmOpen(false);
  };
  const onChange = (event: SelectChangeEvent) => {
    setReason(event.target.value as BlockIgnoreReason);
  };
  const saveIgnore = async () => {
    const additionalBlocksToIgnore: number[] =
      hit.collapsedHits.length > 0 && ignoreCollapsedHits ? hit.collapsedHits.map((ch) => ch.blockIndex) : [];
    return await backend
      .markBlocksAsIgnored(hit.block.documentId, [hit.block.index, ...additionalBlocksToIgnore], reason)
      .then(() => {
        handleClose();
        onHitIgnored(hit);
        Pendo.event("dismiss_hit", {});
      })
      .catch(() => {
        enqueueSnackbar("Unable to dismiss", { variant: "error" });
      });
  };

  const ignoreHit = () => {
    saveIgnore();
    handleClose();
  };

  return (
    <Box>
      <IconButton size="small" onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={
          popoverOrigin?.anchorOrigin || {
            vertical: "top",
            horizontal: "left",
          }
        }
        transformOrigin={
          popoverOrigin?.transformOrigin || {
            vertical: "top",
            horizontal: "left",
          }
        }>
        <MenuItem onClick={() => setConfirmOpen(true)}>Dismiss</MenuItem>
      </Menu>
      <Dialog
        open={confirmOpen}
        TransitionProps={{
          onExited: () => setConfirmOpen(false),
        }}>
        <DialogTitle>Dismiss</DialogTitle>
        <DialogContent>
          <Box>
            <P sx={{ marginBottom: 3 }} variant="body2">
              Are you sure want to dismiss this block of text? It will not be used in any future findings.
            </P>
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select value={reason} onChange={onChange} label="Reason">
                <MenuItem value="Duplicate">Duplicate</MenuItem>
                <MenuItem value="Bad Match">Bad Match</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={ignoreHit}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LensHitActions;
